import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar";
import Header from "../Header";
import "./Services.css";
import axios from "axios";
import API_BASE_URL from "../../config";
import {
  BsPhoneFill,
  BsWhatsapp,
  BsChatFill,
  BsBagPlus,
  BsXLg,
  BsCheck2,
  BsCheckCircleFill,
  BsXCircleFill,
  BsPlusLg,
} from "react-icons/bs";

const getToken = () => localStorage.getItem("userToken");
const META = {
  sms: { icon: BsPhoneFill, color: "#7c7cff" },
  whatsapp: { icon: BsWhatsapp, color: "#25d366" },
  imessage: { icon: BsChatFill, color: "#0d6efd" },
};

export default function Services() {
  const navigate = useNavigate();
  const headers = useCallback(() => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const [catalog, setCatalog] = useState([]);
  const [active, setActive] = useState({});
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { if (!getToken()) window.location.href = "/login"; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/services`, { headers: headers(), timeout: 15000 });
      setCatalog(res.data.catalog || []);
      setActive(res.data.services || {});
      setCounts(res.data.counts || {});
    } catch (err) {
      console.error("Services load error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  // Toast when returning from Stripe Checkout.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("checkout") === "success") flash("success", "Subscription active — thank you!");
    else if (q.get("checkout") === "cancel") flash("error", "Checkout cancelled.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flash = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 2800); };

  const openPicker = (svc) => {
    setPicker(svc.service);
    const current = active[svc.service]?.plan;
    const match = svc.plans.find((p) => p.name === current);
    setChosen(match ? match.id : svc.plans[0].id);
  };

  const subscribe = async (service) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/services/${service}/subscribe`, { plan: chosen },
        { headers: headers(), timeout: 15000 });
      if (res.data.url) { window.location.href = res.data.url; return; } // → Stripe Checkout
      if (res.data.alreadyActive) {
        flash("success", `${service} is already active — add numbers from the ${service} page (each adds to your bill).`);
        setPicker(null);
        return;
      }
      flash("success", `${service} activated (${res.data.plan})`);
      setPicker(null);
      await load();
    } catch (err) {
      flash("error", err.response?.data?.error || "Failed to subscribe.");
    } finally { setBusy(false); }
  };

  const cancel = async (service) => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE_URL}/services/${service}/cancel`, {}, { headers: headers(), timeout: 15000 });
      flash("success", `${service} cancelled`);
      setPicker(null);
      await load();
    } catch (err) {
      flash("error", err.response?.data?.error || "Failed to cancel.");
    } finally { setBusy(false); }
  };

  return (
    <div className="page-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4">
          <div className="page-header">
            <h4>Services</h4>
            <p className="page-subtitle">Turn channels on or off and manage what's active on your account.</p>
          </div>

          {loading ? (
            <div className="page-loading"><div className="spinner" /><p>Loading services…</p></div>
          ) : (
            <>
              <div className="svc-grid3">
                {catalog.map((svc) => {
                  const meta = META[svc.service] || { icon: BsBagPlus, color: "#6b7280" };
                  const Icon = meta.icon;
                  const isActive = active[svc.service]?.status === "active";
                  const plan = active[svc.service]?.plan;
                  const count = counts[svc.service] || 0;
                  const unitWord = svc.service === "whatsapp" ? "number" : svc.service === "imessage" ? "handle" : "sender";
                  const statusText = isActive
                    ? (svc.service === "sms" ? plan : `${count} ${unitWord}${count === 1 ? "" : "s"}`)
                    : "";
                  const syncing = svc.service === "imessage" && isActive;
                  return (
                    <div className="svc2" key={svc.service}>
                      <div className="svc2-top">
                        <div className="svc2-ic" style={{ background: meta.color + "22", color: meta.color }}><Icon /></div>
                        <span className="svc2-name">{svc.name}</span>
                        <button className={`svc2-tog ${isActive ? "on" : ""}`} onClick={() => openPicker(svc)} aria-label={`Toggle ${svc.name}`} />
                      </div>
                      <p className="svc2-desc">{svc.description}</p>
                      <div className="svc2-foot">
                        <span className={`svc2-st ${isActive ? (syncing ? "warn" : "ok") : "off"}`}>
                          <span className="d" />{isActive ? `${syncing ? "Syncing" : "Active"} · ${statusText}` : "Not subscribed"}
                        </span>
                        <button className="svc2-mng" onClick={() => openPicker(svc)}>Manage →</button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="svc2-addons">
                <h6 className="svc2-addons-h">Add-ons</h6>
                <div className="svc2-addon">
                  <div className="svc2-addon-ic"><BsPlusLg /></div>
                  <div>
                    <div className="svc2-addon-nm">Extra WhatsApp number</div>
                    <div className="svc2-addon-sub">$25 / month per number</div>
                  </div>
                  <button className="svc2-add" onClick={() => navigate("/whatsapp")}>Add</button>
                </div>
                <div className="svc2-addon">
                  <div className="svc2-addon-ic"><BsPlusLg /></div>
                  <div>
                    <div className="svc2-addon-nm">Extra iMessage handle</div>
                    <div className="svc2-addon-sub">$49 / month per handle</div>
                  </div>
                  <button className="svc2-add" onClick={() => navigate("/imessage")}>Add</button>
                </div>
              </div>
            </>
          )}

          {picker && (() => {
            const svc = catalog.find((c) => c.service === picker);
            const meta = META[picker];
            const Icon = meta.icon;
            const isActive = active[picker]?.status === "active";
            return (
              <div className="svc-picker">
                <div className="svc-picker-head">
                  <strong><Icon style={{ color: meta.color }} /> {svc.name} — choose a plan</strong>
                  <button className="svc-x" onClick={() => setPicker(null)} aria-label="Close"><BsXLg /></button>
                </div>
                <div className="svc-plans">
                  {svc.plans.map((p) => (
                    <div
                      key={p.id}
                      className={`svc-plan ${chosen === p.id ? "sel" : ""}`}
                      style={chosen === p.id ? { borderColor: meta.color } : {}}
                      onClick={() => setChosen(p.id)}
                    >
                      <div className="svc-plan-name">{p.name}</div>
                      <div className="svc-plan-price">${p.price}<span>{p.perNumber ? "/number · mo" : "/mo"}</span></div>
                      <ul>{p.features.map((f, i) => <li key={i}><BsCheck2 /> {f}</li>)}</ul>
                    </div>
                  ))}
                </div>
                <div className="svc-picker-actions">
                  <button
                    className="svc-subscribe-btn"
                    style={{ background: meta.color, borderColor: meta.color }}
                    disabled={busy}
                    onClick={() => subscribe(picker)}
                  >
                    {isActive ? "Update subscription" : "Subscribe"}
                  </button>
                  {isActive && (
                    <button className="svc-cancel-btn" disabled={busy} onClick={() => cancel(picker)}>Cancel service</button>
                  )}
                  <button className="svc-close-btn" onClick={() => setPicker(null)}>Close</button>
                </div>
                {svc.pricing?.model === "per_number" ? (
                  <div className="svc-demo">
                    Billed <strong>${svc.pricing.unit}/number</strong>, unlimited numbers. After subscribing, add numbers from the{" "}
                    <strong>{svc.name}</strong> page — each adds ${svc.pricing.unit}/mo.
                    {picker === "imessage" && " (We provision the Mac for each iMessage number.)"}
                    <br />No payment connected yet — subscribing grants access immediately.
                  </div>
                ) : (
                  <div className="svc-demo">No payment connected yet — subscribing grants access immediately.</div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {toast && (
        <div className={`toast-notification ${toast.type === "success" ? "toast-success" : "toast-error"}`}>
          {toast.type === "success" ? <BsCheckCircleFill /> : <BsXCircleFill />}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
